import { ArrowDownRight, ArrowUpRight, Calendar, ArrowRight, Inbox } from "lucide-react";

/* ============ PAGE HEADER ============ */
export function PageHeader({ title, eyebrow, badge, children }) {
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div>
        {/* {eyebrow && <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-1.5">{eyebrow}</div>} */}
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{title}</h1>
        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
          <Calendar className="size-3.5" />
          {today}
          {badge && (
            <span className="ml-1 px-2.5 py-0.5 rounded-full bg-sm-primary text-sm-primary-foreground text-[10px] font-bold tracking-wider">{badge}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}

/* ============ CARD ============ */
export function Card({ children, className = "", title, action, icon, accent }) {
  return (
    <section className={`relative rounded-xl bg-sm-card border border-border shadow-sm overflow-hidden ${className}`}>
      {accent && <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: accent }} />}
      {(title || action) && (
        <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-sm-card sticky top-0 z-10">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {icon}
            <span>{title}</span>
          </div>
          {action}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

/* ============ SECTION HEADER (inside cards or page) ============ */
export function SectionHeader({ icon, label, count, countTone = "neutral", action }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      {icon && <span className="text-muted-foreground">{icon}</span>}
      <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</span>
      {count !== undefined && <StatusBadge tone={countTone}>{count}</StatusBadge>}
      <div className="flex-1" />
      {action}
    </div>
  );
}

/* ============ STAT CARD ============ */
const statBorder = {
  good: "border-l-emerald-500",
  warn: "border-l-amber-400",
  alert: "border-l-red-500",
  neutral: "border-l-blue-400",
};
const statText = {
  good: "text-emerald-600",
  warn: "text-amber-600",
  alert: "text-red-600",
  neutral: "text-blue-600",
};
export function StatCard({ label, value, sub, tone = "neutral", trend, trendUp = true, valueClass, children }) {
  return (
    <div className={`relative rounded-xl bg-sm-card border border-border border-l-4 ${statBorder[tone]} shadow-sm p-3 hover:shadow-md transition-shadow`}>
      <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">{label}</div>
      <div className={`mt-2 text-3xl font-bold tracking-tight ${valueClass ?? ""}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
      {children}
      {trend && (
        <span className={`absolute bottom-3 right-3 inline-flex items-center gap-0.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${trendUp ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
          {trendUp ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />}{trend}
        </span>
      )}
      <span className="sr-only">{statText[tone]}</span>
    </div>
  );
}

/* Backward-compat shim */
export function KPI({ label, value, delta, deltaPositive = true, sub, accent }) {
  const tone = accent === "destructive" ? "alert" : accent === "success" ? "good" : accent === "accent" ? "warn" : "neutral";
  return <StatCard label={label} value={value} sub={sub} tone={tone} trend={delta} trendUp={deltaPositive} />;
}

/* ============ STATUS BADGE ============ */
const badgeMap = {
  good: "bg-emerald-100 text-emerald-700",
  warn: "bg-amber-100 text-amber-700",
  alert: "bg-red-100 text-red-700",
  info: "bg-blue-100 text-blue-700",
  accent: "bg-orange-100 text-orange-700",
  neutral: "bg-muted text-muted-foreground",
};
export function StatusBadge({ children, tone = "neutral", className = "" }) {
  return <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${badgeMap[tone]} ${className}`}>{children}</span>;
}

/* Backward-compat shim mapping old API to new tones */
export function StatusPill({ children, tone = "neutral" }) {
  const map = { neutral: "neutral", success: "good", warning: "warn", destructive: "alert", info: "info", accent: "accent" };
  return <StatusBadge tone={map[tone]}>{children}</StatusBadge>;
}

/* ============ PROGRESS BAR (auto color) ============ */
export function ProgressBar({ value, tone, showLabel = false, className = "" }) {
  let color = "bg-emerald-500";
  if (tone === "warn" || tone === "warning") color = "bg-amber-500";
  else if (tone === "alert" || tone === "destructive") color = "bg-red-500";
  else if (tone === "info") color = "bg-blue-500";
  else if (tone === "primary") color = "bg-sm-primary";
  else if (tone === "accent") color = "bg-orange-500";
  else if (!tone) {
    if (value >= 85) color = "bg-red-500";
    else if (value >= 70) color = "bg-amber-500";
    else color = "bg-emerald-500";
  }
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
      </div>
      {showLabel && <span className="text-sm font-medium tabular-nums w-10 text-right">{value}%</span>}
    </div>
  );
}

/* ============ AVATAR ============ */
export function Avatar({ initials, tone = "primary", size = 36 }) {
  const tones = {
    primary: "bg-sm-primary/15 text-sm-primary",
    accent: "bg-orange-100 text-orange-700",
    good: "bg-emerald-100 text-emerald-700",
    success: "bg-emerald-100 text-emerald-700",
    info: "bg-blue-100 text-blue-700",
    warn: "bg-amber-100 text-amber-700",
    warning: "bg-amber-100 text-amber-700",
    alert: "bg-red-100 text-red-700",
    destructive: "bg-red-100 text-red-700",
  };
  return (
    <div
      className={`rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${tones[tone] ?? tones.primary}`}
      style={{ width: size, height: size }}
    >
      {initials}
    </div>
  );
}

/* ============ ACTION BUTTONS ============ */
export function ApproveBtn({ children = "Approve", onClick }) {
  return <button onClick={onClick} className="px-3 py-1 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">{children}</button>;
}
export function RejectBtn({ children = "Reject", onClick }) {
  return <button onClick={onClick} className="px-3 py-1 rounded-lg text-sm font-medium border border-red-300 text-red-600 hover:bg-red-50 transition-colors">{children}</button>;
}
export function GhostBtn({ children, onClick, className = "" }) {
  return <button onClick={onClick} className={`px-3 py-1 rounded-lg text-sm font-medium border border-border text-foreground hover:bg-muted transition-colors ${className}`}>{children}</button>;
}

/* ============ LIST ROW ============ */
export function ListRow({ leading, title, sub, badge, action, tone, className = "", onClick }) {
  const bg = tone === "alert" ? "bg-red-50/60" : tone === "warn" ? "bg-amber-50/60" : tone === "good" ? "bg-emerald-50/40" : "";
  const left = tone === "alert" ? "border-l-4 border-red-500" : tone === "warn" ? "border-l-4 border-amber-400" : tone === "good" ? "border-l-4 border-emerald-500" : tone === "info" ? "border-l-4 border-blue-400" : "";
  const clickable = typeof onClick === "function";
  const handleKeyDown = (event) => {
    if (!clickable) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick(event);
    }
  };

  return (
    <div
      className={`flex items-center gap-3 py-3 px-3 -mx-3 border-b border-border/50 last:border-0 ${clickable ? "cursor-pointer hover:bg-muted/40 transition-colors" : ""} ${bg} ${left} ${className}`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      {leading}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm leading-tight truncate">{title}</div>
        {sub && <div className="text-xs text-muted-foreground mt-0.5 truncate">{sub}</div>}
      </div>
      {badge}
      {action}
    </div>
  );
}

/* ============ EMPTY STATE ============ */
export function EmptyState({ icon, label = "No items", sub }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <div className="size-12 rounded-2xl bg-muted flex items-center justify-center mb-3 text-muted-foreground">
        {icon ?? <Inbox className="size-5" />}
      </div>
      <div className="text-sm text-muted-foreground font-medium">{label}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

/* ============ METRIC PILL (for inline rows) ============ */
export function MetricPill({ label, value, tone = "neutral" }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${badgeMap[tone].replace("text-", "border-").replace(/-(100)/g, "-200")} bg-sm-card border`}>
      <span className="text-base font-bold">{value}</span>
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</span>
    </div>
  );
}

/* ============ SPARKLINE (legacy) ============ */
export function Sparkline({ data, color = "var(--primary)", height = 40 }) {
  const w = 100, h = height;
  const min = Math.min(...data), max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * (h - 4) - 2}`).join(" ");
  const area = `0,${h} ${pts} ${w},${h}`;
  const id = `grad-${color.replace(/[^a-z0-9]/gi, "")}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full" style={{ height }}>
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#${id})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/* ============ CIRCULAR RING ============ */
export function Ring({ value, size = 80, stroke = 8, color = "var(--primary)", track = "var(--muted)", label }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (value / 100) * c;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke={track} strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} />
      </svg>
      {label && <div className="absolute inset-0 flex items-center justify-center text-sm font-bold">{label}</div>}
    </div>
  );
}
